import React, { useEffect, useState, memo, useCallback } from 'react';

//external packages
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { store } from 'redux-rest-reducer';
import { bindActionCreators } from 'redux';

//internal UI packages
import { PreClassEnterModal, AV_METRIC_EVENT } from '@cuemath/intel';
import { post as addSHCData } from 'actions/add-health-check-data';

import { usePubnub } from '../../../contexts/pubnub-context';

const PreClassScreen = ({ onEnterClass, classStatus, metricLogger, userId, actions }) => {
  const { teacherClassroomId } = useParams();
  const pubnub = usePubnub();
  const { scheduled_start_datetime: classStartTime, status: teacherStatus } = classStatus;
  const [showTimer, setShowTimer] = useState(true);
  const [channels] = useState([teacherClassroomId]);
  const [isClassStarted, setClassStarted] = useState(false);

  const onContinue = () => {
    console.log('onContinue');
  };

  const enterClass = () => {
    onEnterClass();
  };

  const logMetric = useCallback(
    (event, data) => {
      metricLogger(event, {
        ...data,
        time: new Date().getTime(),
      });
    },
    [metricLogger],
  );

  const onAudioError = error => {
    logMetric(AV_METRIC_EVENT.MEDIA_DENIED_ERROR, {
      message: `Audio Error | ${JSON.stringify(error)}`,
      error,
    });
  };

  const onCameraError = error => {
    logMetric(AV_METRIC_EVENT.MEDIA_DENIED_ERROR, {
      message: `Camera Error | ${JSON.stringify(error)}`,
      error,
    });
  };

  const onCameraSuccess = () => {
    logMetric(AV_METRIC_EVENT.MEDIA_CAMERA_SUCCESS);
  };

  const onAudioSuccess = () => {
    logMetric(AV_METRIC_EVENT.MEDIA_AUDIO_SUCCESS);
  };

  const onEnteredPreviewScreen = () => {
    logMetric(AV_METRIC_EVENT.ENTERED_PREVIEW_SCREEN);
  };

  const logEvent = (type, data) => {
    switch (type) {
      case 'timer_completed':
        setShowTimer(false);
        break;
      case 'join_late':
        setShowTimer(false);
        break;
      case 'audio_error':
        onAudioError(data);
        break;
      case 'camera_error':
        onCameraError(data);
        break;
      case 'camera_success':
        onCameraSuccess();
        break;
      case 'audio_success':
        onAudioSuccess();
        break;
      case 'entered_preview_screen':
        onEnteredPreviewScreen();
        break;

      default:
        break;
    }
  };

  const handleMessage = event => {
    const { eventType, message } = event.message;
    if (eventType === 'CLASS_STARTED' && message === 'CLASS_STARTED') {
      setClassStarted(true);
    }
  };

  useEffect(() => {
    if (teacherStatus === 'P') {
      setClassStarted(true);
    }
  }, [teacherStatus]);

  useEffect(() => {
    const listener = { message: handleMessage };
    pubnub.addListener(listener);
    pubnub.subscribe({ channels });
    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({
        channels: channels,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userId && teacherClassroomId) {
      const callId = teacherClassroomId;
      const userCallId = `${callId}_${userId}_${Number(new Date())}`;

      logMetric(AV_METRIC_EVENT.PREVIEW_SCREEN_LOADED, {
        call_type_intent_id: callId,
        user_call_type_intent_id: userCallId,
      });
    }
  }, [userId, teacherClassroomId, logMetric]);

  const clientValidationError = showTimer
    ? 'Waiting for Teacher to start the class!'
    : 'Teacher has not started the class!';

  const processMediaPermissionsData = data => {
    const shcDataPost = store.post();

    if (data) {
      actions.addSHCData(shcDataPost.id, {
        user_id: userId,
        user_type: 'ACADEMIC_COUNSELOR',
        classroom_id: teacherClassroomId,
        meta: {
          ...data,
          browser_and_device: navigator.userAgent,
        },
      });
    }
  };

  return (
    <PreClassEnterModal
      nextClassStartTime={classStartTime}
      onClickContinue={onContinue}
      onClickEnterClass={enterClass}
      trackEvent={logEvent}
      showContinue={false}
      onCloseModal={onContinue}
      showTimer={showTimer}
      showTips={true}
      clientValidationRequired={true}
      clientValidated={isClassStarted}
      clientValidationError={clientValidationError}
      getMediaPermissionsData={processMediaPermissionsData}
    />
  );
};

export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      addSHCData,
    },
    dispatch,
  ),
}))(memo(PreClassScreen));
